import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { ShieldCheck, Leaf, ArrowRight, Flower2 } from "lucide-react";

const DetailBlock = ({ label, children }) => (
  <div className="mt-4">
    <p className="text-[10px] uppercase tracking-widest text-[#C05C42] mb-1">{label}</p>
    <p className="text-sm text-[#5C6359] leading-relaxed">{children}</p>
  </div>
);

const ROLE_BADGES = {
  steward: { cls: "bg-[#E7EEE3] text-[#3F6B45] border-[#BBD0B7]", Icon: Leaf, label: "Vitality Steward" },
  healer: { cls: "bg-[#EFE3EC] text-[#7A4A6B] border-[#D8C0D2]", Icon: Flower2, label: "Traditional Healer" },
  clinician: { cls: "bg-[#F1E4DE] text-[#A04A33] border-[#E0C7BC]", Icon: ShieldCheck, label: "Clinician" },
};

// Rotating botanical defaults so any steward/healer without a photo still looks polished.
const DEFAULT_BOTANICALS = [
  "https://static.prod-images.emergentagent.com/jobs/b00ff456-a9c2-41e9-83a6-e7571ddb543a/images/97fc507657ddcdee02bb79707b34c06d607d721c3ff1c443f970f9f12bf6d646.png",
  "https://static.prod-images.emergentagent.com/jobs/b00ff456-a9c2-41e9-83a6-e7571ddb543a/images/51cbe173a4c26d83d379fdc0b48b2bcb94497de800f476699fe1d260650c1ea9.png",
  "https://static.prod-images.emergentagent.com/jobs/b00ff456-a9c2-41e9-83a6-e7571ddb543a/images/4181e8032dbd9974c3bb76b0cb711b06c99ac444b893180e0f902bd43cbc6874.png",
  "https://static.prod-images.emergentagent.com/jobs/b00ff456-a9c2-41e9-83a6-e7571ddb543a/images/e6b8a1df087ad2207d91858c23dabada7ea6200485a44387039682cd23890414.png",
];

const cardImage = (p) => {
  if (p.image) return p.image;
  let h = 0;
  for (const ch of (p.id || p.name || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return DEFAULT_BOTANICALS[h % DEFAULT_BOTANICALS.length];
};

const RoleBadge = ({ kind }) => {
  const cfg = ROLE_BADGES[kind] || ROLE_BADGES.clinician;
  const { Icon } = cfg;
  return (
    <span
      data-testid={`role-badge-${kind}`}
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${cfg.cls}`}
    >
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

export default function Practitioners() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/practitioners").then((r) => setList(r.data));
  }, []);

  const counts = list.reduce((acc, p) => { acc[p.kind] = (acc[p.kind] || 0) + 1; return acc; }, {});
  const healerCount = list.filter((p) => p.kind === "healer" || p.also_healer).length;
  const FILTERS = [
    { key: "all", label: "All", n: list.length },
    { key: "clinician", label: "Clinicians", n: counts.clinician || 0 },
    { key: "steward", label: "Vitality Stewards", n: counts.steward || 0 },
    { key: "healer", label: "Traditional Healers", n: healerCount },
  ];
  const visible =
    filter === "all" ? list
      : filter === "healer" ? list.filter((p) => p.kind === "healer" || p.also_healer)
      : list.filter((p) => p.kind === filter);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-12 max-w-6xl mx-auto">
        <p className="label-eyebrow mb-3">Our team</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2D322B] max-w-3xl">
          Your Health &amp; Longevity Professionals.
        </h1>
        <p className="mt-6 text-[#5C6359] max-w-2xl">
          A multidisciplinary circle of clinicians, Vitality Stewards, and traditional &amp;
          Indigenous healers — honouring both precision medicine and ancestral wisdom. Stewards
          offer coaching guidance under clinician supervision; traditional healers practise within
          their cultural lineage. Sovereign members receive priority introductions.
        </p>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 pb-2 max-w-6xl mx-auto">
        <div className="flex flex-wrap gap-2" data-testid="practitioner-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-colors ${
                filter === f.key
                  ? "bg-[#2D322B] text-[#F9F6F0] border-[#2D322B]"
                  : "bg-transparent text-[#5C6359] border-[#D1C7B8] hover:border-[#C05C42] hover:text-[#2D322B]"
              }`}
            >
              {f.label} <span className="opacity-60">({f.n})</span>
            </button>
          ))}
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 pt-8 pb-16 max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
        {visible.map((p) => (
          <article
            key={p.id}
            className="group bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl overflow-hidden flex flex-col transition-all duration-500 hover:border-[#C05C42]/50 hover:shadow-[0_24px_60px_-30px_rgba(45,50,43,0.5)]"
            data-testid={`practitioner-${p.id}`}
          >
            <div className="h-52 bg-gradient-to-b from-[#EFE8DC] to-[#E5DEC3] flex items-center justify-center overflow-hidden relative">
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#7C8B72]">
                <Leaf className="w-12 h-12 opacity-70" />
                <span className="font-serif text-3xl mt-2 text-[#3F6B45]">
                  {(p.name || "").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </span>
              </div>
              <img
                src={cardImage(p)}
                alt={`Illustration representing ${p.name}'s practice`}
                loading="lazy"
                className="relative h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-105"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
            <div className="p-7 flex-1 flex flex-col">
              <div className="flex items-center gap-2 flex-wrap">
                <RoleBadge kind={p.kind} />
                {p.also_healer && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border bg-[#EFE3EC] text-[#7A4A6B] border-[#D8C0D2]" data-testid={`also-healer-badge-${p.id}`}>
                    <Flower2 className="w-3 h-3" /> Traditional Healer
                  </span>
                )}
                {p.kind === "steward" && p.role === "Vitality Steward" && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-[#EDE7DC] text-[#5C6359] border border-[#D1C7B8]" data-testid={`supervised-badge-${p.id}`}>
                    <ShieldCheck className="w-3 h-3" /> Clinician-supervised
                  </span>
                )}
              </div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#C05C42] mt-4">{p.role}</p>
              <h3 className="font-serif text-2xl text-[#2D322B] mt-2 leading-tight">{p.name}</h3>
              {p.credentials && (
                <p className="text-xs text-[#8a7c66] mt-1.5 tracking-wide">{p.credentials}</p>
              )}
              {p.bio && <p className="text-sm text-[#5C6359] mt-4 leading-relaxed">{p.bio}</p>}

              {p.training && <DetailBlock label="Training & Education">{p.training}</DetailBlock>}
              {p.special_interests && <DetailBlock label="Special Interests">{p.special_interests}</DetailBlock>}
              {p.tradition && <DetailBlock label="Tradition / lineage">{p.tradition}</DetailBlock>}
              {p.specialties && <DetailBlock label="Specialties">{p.specialties}</DetailBlock>}
              {p.services && <DetailBlock label="Services Provided">{p.services}</DetailBlock>}
              {p.location && <DetailBlock label={p.kind === "healer" ? "Region" : "Location"}>{p.location}</DetailBlock>}
              {p.contact && <DetailBlock label="Contact">{p.contact}</DetailBlock>}
              {p.supervising_clinician && (
                <DetailBlock label="Supervised by">{p.supervising_clinician}</DetailBlock>
              )}

              {p.kind === "steward" && p.role === "Vitality Steward" && (
                <p className="mt-5 text-[11px] text-[#9A8F7E] italic leading-relaxed border-t border-[#D1C7B8] pt-3" data-testid={`steward-disclaimer-${p.id}`}>
                  Coaching guidance only; not medical advice.
                </p>
              )}
              {p.kind === "healer" && (
                <p className="mt-5 text-[11px] text-[#9A8F7E] italic leading-relaxed border-t border-[#D1C7B8] pt-3" data-testid={`healer-disclaimer-${p.id}`}>
                  Traditional healing practice — offered in cultural context; not a substitute for medical care.
                </p>
              )}
            </div>
          </article>
        ))}
        {visible.length === 0 && (
          <p className="md:col-span-2 text-sm text-[#5C6359] bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-8 text-center" data-testid="practitioner-empty">
            None listed in this circle yet — check back soon.
          </p>
        )}
      </section>

      {/* Join-our-circle CTAs */}
      <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-[#2D322B] text-[#F9F6F0] p-10 flex flex-col justify-between gap-6" data-testid="become-steward-cta">
          <div>
            <p className="label-eyebrow !text-[#D49A44] mb-3">Join our circle</p>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">Become a Vitality Steward.</h2>
            <p className="mt-4 text-[#F9F6F0]/80 leading-relaxed text-sm">
              Non-clinical guides who support members on their longevity journey — under the
              supervision of our clinicians.
            </p>
          </div>
          <Link
            to="/become-a-steward"
            data-testid="become-steward-link"
            className="self-start inline-flex items-center gap-2 bg-[#D49A44] hover:bg-[#c08a36] text-[#2D322B] font-medium px-7 py-3 rounded-full transition-colors"
          >
            Apply now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="rounded-3xl bg-[#3B2C38] text-[#F9F6F0] p-10 flex flex-col justify-between gap-6" data-testid="become-healer-cta">
          <div>
            <p className="label-eyebrow !text-[#D8A7CB] mb-3">A conduit for ancestral wisdom</p>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">List as a Traditional Healer.</h2>
            <p className="mt-4 text-[#F9F6F0]/80 leading-relaxed text-sm">
              We curate Indigenous and traditional healers as a respectful, one-stop point of
              contact for our members. Share your practice and lineage to be considered.
            </p>
          </div>
          <Link
            to="/become-a-healer"
            data-testid="become-healer-link"
            className="self-start inline-flex items-center gap-2 bg-[#D8A7CB] hover:bg-[#c994bb] text-[#3B2C38] font-medium px-7 py-3 rounded-full transition-colors"
          >
            Apply to be listed <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
